import { useEffect, useRef, useState } from "react";
import type { Beat } from "../scenario/types";
import { audio } from "../lib/audio";
import { useSim, sliceStopsNext } from "../state/store";
import { ConsequenceVisual } from "./ConsequenceVisual";
import { BadUsbInfoModal } from "../devices/BadUsbInfoModal";
import { activeTtsLang } from "../lib/locale";
import { SkipButton } from "./SkipButton";
import { tx, txr } from "../lib/tx";

/** Consequence visuals that dramatize an attack actively unfolding (a payload
 *  running, a break-in in progress, a wire being laundered) get the scary
 *  sting bed instead of the course's calm scene music — see
 *  `audio.switchToDramatic()`. Every other consequence keeps normal ambient. */
const DRAMATIC_VISUALS = new Set(["badusb", "tailgate_breach", "hackerterminal", "fake_meeting"]);

/**
 * "What just happened" cinematic replay — the VISUAL half of the
 * consequence-and-rewind teaching loop. Shown right after a risky decision,
 * BEFORE the teach card explains it, so the learner sees the consequence
 * play out (security-camera footage, in this course) rather than only
 * reading about it. A `teach` beat with `outcome:"risky"` still follows and
 * does the explaining; this beat is purely the dramatization.
 *
 * Lines are spoken + revealed SEQUENTIALLY (same pattern as TechRequest.tsx)
 * so they never overlap; Continue is disabled until the last line has shown.
 */
/** Circular alert/radar glyph replacing the old plain "!" dot on the "what
 *  just happened" card — three concentric red rings (CSS-animated pulse/
 *  rotation, see `.cq-alert-ring*` in styles.css) around a solid warning
 *  triangle core, echoing the intro card's `.intro-radar` sweep but in the
 *  attack-red palette instead of teal. Pure CSS + one small inline SVG, no
 *  new asset. */
function CqAlertIcon() {
  return (
    <span className="cq-alert-ico" aria-hidden>
      <span className="cq-alert-ring cq-alert-ring--3" />
      <span className="cq-alert-ring cq-alert-ring--2" />
      <span className="cq-alert-ring cq-alert-ring--1" />
      <span className="cq-alert-sweep" />
      <span className="cq-alert-core">
        <svg viewBox="0 0 24 24" fill="none" focusable="false">
          <path d="M12 3.5 22 20.5H2Z" fill="currentColor" />
          <path d="M12 10v4.2" stroke="#1a0505" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="12" cy="17.3" r=".9" fill="#1a0505" />
        </svg>
      </span>
    </span>
  );
}

/** Full-width mint-gradient continue button (matches the intro card's
 *  `.cf-cta` CTA language) with a trailing arrow that nudges forward on
 *  hover — replaces the old plain `.btn` treatment on the consequence
 *  card's Continue action. */
function CqContinueButton({
  done,
  label,
  onClick,
}: {
  done: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`btn cq-continue ${done ? "" : "cq-continue--wait"}`}
      disabled={!done}
      onClick={onClick}
    >
      <span className="cq-continue-label">{label}</span>
      {done && (
        <span className="cq-continue-arrow" aria-hidden>
          {"\u2192"}
        </span>
      )}
    </button>
  );
}

function fmtClock(totalSeconds: number): string {
  // Starts the on-screen "recording" clock at a fixed late-evening time so it
  // reads like a real timestamped feed rather than an elapsed-time counter.
  const base = 22 * 3600 + 14 * 60 + 0;
  const total = base + totalSeconds;
  const h = Math.floor(total / 3600) % 24;
  const m = Math.floor(total / 60) % 60;
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function Consequence({ beat, onContinue }: { beat: Beat; onContinue: () => void }) {
  const cfg = beat.consequence;
  const [shown, setShown] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const started = useRef(false);
  const alive = useRef(true);
  const autoTimer = useRef<number | null>(null);
  const continued = useRef(false);

  const goNext = () => {
    if (continued.current) return;
    continued.current = true;
    if (autoTimer.current != null) window.clearTimeout(autoTimer.current);
    onContinue();
  };

  useEffect(() => {
    alive.current = true;
    started.current = false;
    continued.current = false;
    setShown(0);
    setShowInfo(false);
    if (cfg?.visual && DRAMATIC_VISUALS.has(cfg.visual)) {
      audio.switchToDramatic();
    }
    return () => {
      alive.current = false;
      if (autoTimer.current != null) window.clearTimeout(autoTimer.current);
      audio.cancelSpeech();
      if (cfg?.visual && DRAMATIC_VISUALS.has(cfg.visual)) {
        audio.restoreSceneAmbient(useSim.getState().scenario ?? undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beat.id]);

  // Ticking REC timestamp for the CCTV chrome — cosmetic only, resets per beat.
  useEffect(() => {
    setElapsed(0);
    const id = window.setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => window.clearInterval(id);
  }, [beat.id]);

  useEffect(() => {
    if (!cfg || started.current) return;
    started.current = true;
    // Prefetch ALL lines up front (same pattern as TechRequest.tsx) so the
    // TTS fetch for line 2+ has already completed by the time it's needed —
    // otherwise every line boundary incurs its own network round-trip,
    // which reads as a dead-air "pause" between narration + animation. The
    // very first line still shares this same request via audio.ts's
    // in-flight cache dedupe (see fetchTts), so nothing is fetched twice.
    void Promise.all(cfg.lines.map((t) => audio.prefetchSpeech(t, { voice: "onyx", lang: activeTtsLang() })));
    let i = 0;
    const next = async () => {
      if (!alive.current || !cfg.lines[i]) {
        if (alive.current && cfg.autoContinueMs != null) {
          autoTimer.current = window.setTimeout(() => {
            if (alive.current) goNext();
          }, cfg.autoContinueMs);
        }
        return;
      }
      setShown(i + 1);
      await audio.speak(cfg.lines[i], { voice: "onyx", lang: activeTtsLang() });
      i += 1;
      if (alive.current) next();
    };
    audio.breachAlert();
    next();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beat.id, cfg]);

  if (!cfg) return null;
  const scenario = useSim.getState().scenario;
  const returnHome = !!scenario && sliceStopsNext(scenario, beat.id);
  const cctv = cfg.cctv !== false;
  const done = shown >= cfg.lines.length;
  const channel = cfg.channel ?? "03";
  const rulerTicks = ["15", "10", "05", "00", "-05", "-10", "-15"];

  const skip = () => {
    audio.cancelSpeech();
    setShown(cfg.lines.length);
    goNext();
  };

  const card = (
    <div className="cq-card">
      <span className="cq-card-corner cq-card-corner--tl" aria-hidden />
      <span className="cq-card-corner cq-card-corner--br" aria-hidden />
      <div className="cq-head">
        <CqAlertIcon />
        <div className="cq-head-main">
          <div className="learn-eyebrow attack">{cfg.eyebrow ?? tx("WHAT JUST HAPPENED")}</div>
          <h2 className="cq-title">{cfg.title}</h2>
          <div className="cq-sep" aria-hidden />
        </div>
      </div>
      <div className="cq-lines">
        {cfg.lines.slice(0, shown).map((l, i) => (
          <p key={i} className="cq-line">
            {l}
          </p>
        ))}
      </div>
      {cfg.visual === "badusb" ? (
        <div className="cq-actions cq-actions--pair">
          <button type="button" className="cq-info-btn" onClick={() => setShowInfo(true)}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
              <path d="M12 8.4h.01M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            {tx("What is a BadUSB attack?")}
          </button>
          <div className="cq-continue-group">
            <CqContinueButton
              done={done}
              label={done ? (returnHome ? tx("Back to the terminal") : cfg.continueLabel ?? tx("Continue")) : tx("See why this matters →")}
              onClick={goNext}
            />
            <SkipButton onClick={skip} />
          </div>
        </div>
      ) : (
        <div className="cq-continue-group">
          <CqContinueButton
            done={done}
            label={done ? (returnHome ? tx("Back to the terminal") : cfg.continueLabel ?? tx("Continue")) : tx("See why this matters →")}
            onClick={goNext}
          />
          <SkipButton onClick={skip} />
        </div>
      )}
    </div>
  );

  if (!cctv) {
    return (
      <div className={`consequence ${cfg.visual ? "consequence--visual" : ""} ${cfg.visual === "fake_meeting" ? "consequence--scene" : ""}`}>
        {cfg.visual && <ConsequenceVisual kind={cfg.visual} stage={shown} hypothetical={cfg.hypothetical} />}
        {card}
        {showInfo && <BadUsbInfoModal onClose={() => setShowInfo(false)} />}
      </div>
    );
  }

  return (
    <div className={`consequence consequence--cctv ${cfg.visual ? "consequence--visual" : ""}`}>
      {/* Physical monitor casing: dark frame + corner screws + a left-edge
          waveform ruler + a bottom OSD status bar, matching a real
          surveillance-monitor bezel. Everything the camera "sees" (the
          scene illustration, scanline/noise/vignette overlay, and the
          narration card) lives inside .cq-screen so it never bleeds under
          the bezel chrome. */}
      <div className="cq-bezel">
        <span className="cq-bezel-screw cq-bezel-screw--tl" aria-hidden />
        <span className="cq-bezel-screw cq-bezel-screw--tr" aria-hidden />
        <span className="cq-bezel-screw cq-bezel-screw--bl" aria-hidden />
        <span className="cq-bezel-screw cq-bezel-screw--br" aria-hidden />
        <div className="cq-ruler" aria-hidden>
          {rulerTicks.map((t) => (
            <span key={t} className={`cq-ruler-tick ${t === "00" ? "active" : ""}`}>
              {t === "00" ? (
                <>
                  <span className="cq-ruler-arrow">{"\u25B8"}</span>
                  {t}
                  <span className="cq-ruler-arrow">{"\u25C2"}</span>
                </>
              ) : (
                t
              )}
            </span>
          ))}
        </div>
        <div className="cq-statusbar">
          <span className="cq-statusbar-left">
            <span className="cq-statusbar-ico" aria-hidden>{"\u2637"}</span>
            {tx("RES: 1920x1080  ·  FPS: 30  ·  BITRATE: 6144 kbps")}
          </span>
          <span className="cq-statusbar-right">
            {tx("STATUS: SECURE")}
            <svg className="cq-signal-ico" viewBox="0 0 20 14" aria-hidden focusable="false">
              <rect x="0" y="9" width="3" height="5" fill="currentColor" />
              <rect x="5" y="6" width="3" height="8" fill="currentColor" />
              <rect x="10" y="3" width="3" height="11" fill="currentColor" />
              <rect x="15" y="0" width="3" height="14" fill="currentColor" />
            </svg>
          </span>
        </div>
        <div className="cq-screen">
          {cfg.visual && (
            <div className="cq-glitch-img">
              <ConsequenceVisual kind={cfg.visual} stage={shown} hypothetical={cfg.hypothetical} />
            </div>
          )}
          <div className="cq-cctv" aria-hidden>
            <div className="cq-tint" />
            <div className="cq-noise" />
            <div className="cq-scan" />
            <div className="cq-roll" />
            <div className="cq-flicker" />
            <span className="cq-corner cq-corner--tl" />
            <span className="cq-corner cq-corner--tr" />
            <span className="cq-corner cq-corner--bl" />
            <span className="cq-corner cq-corner--br" />
            <div className="cq-vig" />
          </div>
          <div className="cq-meta">
            <svg className="cq-cam-ico" viewBox="0 0 24 24" aria-hidden focusable="false">
              <path
                d="M4 8a2 2 0 0 1 2-2h2.1l.9-1.35A1.5 1.5 0 0 1 10.25 4h3.5a1.5 1.5 0 0 1 1.25.65L15.9 6H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <circle cx="12" cy="12.6" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className="cq-meta-lines">
              <span className="cq-meta-title">{cfg.kicker ?? tx("SECURITY CAMERA")}</span>
              <span className="cq-meta-sub">{tx("FEED LIVE")}</span>
              <span className="cq-meta-sub">{txr("CHANNEL {channel}", { channel })}</span>
            </span>
          </div>
          <div className="cq-timestamp">
            <span className="cq-rec-row">
              <span className="cq-rec" aria-hidden /> {tx("REC")}
            </span>
            <span className="cq-clock">{fmtClock(elapsed)}</span>
          </div>
          {card}
        </div>
      </div>
      {showInfo && <BadUsbInfoModal onClose={() => setShowInfo(false)} />}
    </div>
  );
}
